import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, UserRound, Save, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { getUser } from "@/lib/user";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISIBILITES = [
  { cle: "anonyme", label: "Anonyme", Icone: UserRound, aide: "Silhouette seulement" },
  { cle: "floutee", label: "Floutée", Icone: EyeOff, aide: "Visible mais floue" },
  { cle: "visible", label: "Visible", Icone: Eye, aide: "Photo en clair" },
];

const Champ = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-stone-500">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const classeInput =
  "w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#FF4757]";

/** Édition du volet Love du profil : ce qui compte vraiment pour une rencontre. */
export default function LoveProfilEdit() {
  const moi = getUser();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // L'administration peut compléter le volet Love d'un profil d'attraction : ?profile_id=…
  const cibleId = params.get("profile_id") || moi?.id;
  const pourAutrui = Boolean(params.get("profile_id")) && params.get("profile_id") !== moi?.id;
  const [refs, setRefs] = useState(null);
  const [form, setForm] = useState({
    taille: "", silhouette: "", interets: [], recherche: "", bio: "", photo_visibilite: "visible",
  });
  const [envoi, setEnvoi] = useState(false);

  useSeo({
    titre: "Mon profil FodindinLove",
    description: "Complétez votre profil Love : taille, silhouette, centres d'intérêt, ce que vous cherchez, et la visibilité de votre photo.",
    chemin: "/love/mon-profil",
  });

  useEffect(() => {
    axios.get(`${API}/love/referentiel`).then((r) => setRefs(r.data)).catch(() => {});
    if (!moi) return;
    axios
      .get(`${API}/love/profils/${cibleId}`)
      .then((r) =>
        setForm({
          taille: r.data.taille || "", silhouette: r.data.silhouette || "",
          interets: r.data.interets || [], recherche: r.data.recherche || "",
          bio: r.data.bio || "", photo_visibilite: r.data.photo_visibilite || "visible",
        }),
      )
      .catch(() => {});
  }, [cibleId]);

  const basculerInteret = (i) =>
    setForm((f) => ({
      ...f,
      interets: f.interets.includes(i)
        ? f.interets.filter((x) => x !== i)
        : [...f.interets, i].slice(0, 8),
    }));

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await axios.put(`${API}/love/profil`, { profile_id: cibleId, ...form });
      toast.success("Profil Love enregistré.");
      navigate(pourAutrui ? "/admin" : "/love");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  if (!moi) {
    return (
      <div className="min-h-screen bg-[#FAF8F5]">
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 pt-40 pb-24 text-center">
          <p className="text-stone-600" data-testid="love-profil-connexion">
            Connectez-vous pour compléter votre profil Love.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 pt-32 pb-24 sm:px-6" data-testid="page-love-profil">
        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FF4757]">
          FodindinLove
        </span>
        <h1 className="mt-2 font-heading text-4xl font-extrabold text-[#2F363F]">
          {pourAutrui ? "Profil Love (administration)" : "Mon profil Love"}
        </h1>
        <p className="mt-3 text-base text-stone-600">
          Ces informations remplacent les champs d'annonce classiques : elles servent aux
          personnes qui cherchent vraiment une rencontre.
        </p>

        {!refs ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF4757]" size={30} /></div>
        ) : (
          <div className="mt-10 space-y-7 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-stone-900/5 sm:p-8">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Champ label="Taille">
                <input value={form.taille} maxLength={10} data-testid="love-input-taille"
                  onChange={(e) => setForm({ ...form, taille: e.target.value })}
                  placeholder="1,75m" className={classeInput} />
              </Champ>
              <Champ label="Silhouette">
                <select value={form.silhouette} data-testid="love-select-silhouette"
                  onChange={(e) => setForm({ ...form, silhouette: e.target.value })}
                  className={classeInput}>
                  <option value="">À préciser</option>
                  {refs.silhouettes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Champ>
            </div>

            <Champ label="Je cherche">
              <div className="flex flex-wrap gap-2">
                {refs.recherches.map((r) => (
                  <button key={r} onClick={() => setForm({ ...form, recherche: r })}
                    data-testid={`love-recherche-${r.replace(/\s/g, "-").toLowerCase()}`}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                      form.recherche === r ? "bg-[#FF4757] text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}>
                    {r}
                  </button>
                ))}
              </div>
            </Champ>

            <Champ label={`Centres d'intérêt (${form.interets.length}/8)`}>
              <div className="flex flex-wrap gap-2">
                {refs.interets.map((i) => (
                  <button key={i} onClick={() => basculerInteret(i)}
                    data-testid={`love-interet-${i.replace(/\s/g, "-").toLowerCase()}`}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                      form.interets.includes(i)
                        ? "bg-[#2F363F] text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}>
                    {i}
                  </button>
                ))}
              </div>
            </Champ>

            <Champ label={`Ma bio (${form.bio.length}/500)`}>
              <textarea value={form.bio} maxLength={500} rows={4} data-testid="love-input-bio"
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Racontez qui vous êtes et ce qui vous fait vibrer…"
                className={`${classeInput} resize-none`} />
            </Champ>

            <Champ label="Visibilité de ma photo">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {VISIBILITES.map(({ cle, label, Icone, aide }) => (
                  <button key={cle} onClick={() => setForm({ ...form, photo_visibilite: cle })}
                    data-testid={`love-visibilite-${cle}`}
                    className={`flex flex-col items-start gap-1 rounded-2xl border p-4 text-left transition-colors ${
                      form.photo_visibilite === cle
                        ? "border-[#FF4757] bg-[#FF4757]/[0.06]"
                        : "border-stone-200 hover:border-stone-400"
                    }`}>
                    <Icone size={18} className={form.photo_visibilite === cle ? "text-[#FF4757]" : "text-stone-500"} />
                    <span className="text-sm font-bold text-[#2F363F]">{label}</span>
                    <span className="text-[11px] text-stone-500">{aide}</span>
                  </button>
                ))}
              </div>
            </Champ>

            <button onClick={enregistrer} disabled={envoi} data-testid="love-enregistrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF4757] px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.01] disabled:opacity-50">
              {envoi ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer mon profil
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
